// Core imports
import * as React from 'react';
import PartClaimService, {
  IPartClaimCanada
} from 'src/services/partClaim/partClaimService';

// Components imports
import Button from '../common/Button';
import DateSingle from 'src/components/common/DateSingle';

// Models imports
import {
  PartClaimModel,
  IWarrantyClaimDetails
} from 'src/model/partClaim.model';

// Style and misc imports
import './WarrantyClaimFormCA.scss';
import * as moment from 'moment';
import UserModel from 'src/model/user.model';

import ClaimStatusModel from 'src/model/ClaimStatus.model';
import CSPFailedDetails from 'src/model/CSPFailedDetails.model';
import ClaimPartDetails from 'src/model/ClaimPartDetails.model';

interface IWarrantyClaimFormCAProps {
  t: any;
  userAccounts: IAccount[];
  onSuccess(): void;
  onFailed(message: string, statusObj?: any): void;
  userData: UserModel;
  cResubmitDetails: ClaimPartDetails;
  RSClaimDetails: CSPFailedDetails;
}

interface IAccount {
  accountId: string;
  name: string;
  email: string;
}

interface IWarrantyClaimFormCAState {
  formData: {
    accountNumber: string;
    accountName: string;
    date: moment.Moment;
    companyName: string;
    customer: {
      address: string;
      city: string;
      name: string;
      phone: string;
      zipCode: string;
      email: string;
    };
    manufacturer: string;
    modelNumber: string;
    serialNumber: string;
    partNumber: string;
    quantity: number;
    price: number;
    invoiceNumber: number;
    datePurchased: moment.Moment;
    customerComplaint: string;
    dateFailed: moment.Moment;
    dateInstalled: moment.Moment;
  };

  component: {
    formValidated: boolean;
    fieldsFailed: string[];
  };

  errors: IFormErrors;
}

class WarrantyClaimFormCA extends React.Component<
  IWarrantyClaimFormCAProps,
  IWarrantyClaimFormCAState
> {
  public PhoneNumberRegEx = new RegExp('^$|(\\+[0-9]{1,3})?[0-9]{10}$');
  public PhoneNumberDashesRegEx = new RegExp('^[0-9. ]*$');
  public emailRegEx = new RegExp('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$');
  public commentsRegEx = new RegExp('^[a-zA-Z0-9_. ]*$');
  constructor(props: IWarrantyClaimFormCAProps) {
    super(props);

    this.state = {
      formData: {
        accountNumber: this.props?.userData?.accountId
          ? this.props.userData.accountId
          : '',
        accountName: '',
        date: null,
        companyName: '',
        customer: {
          address: '',
          city: '',
          name: '',
          phone: '',
          zipCode: '',
          email: ''
        },
        manufacturer: '',
        partNumber: '',
        quantity: 0, // number
        price: 0, // number
        invoiceNumber: 0, // number
        customerComplaint: '',
        datePurchased: null,
        modelNumber: '',
        dateFailed: null,
        dateInstalled: null,
        serialNumber: ''
      },

      component: {
        formValidated: false,
        fieldsFailed: []
      },

      errors: {
        servicerEmailError: false,
        customerPhoneError: false,
        partInstalledDateError: false,
        partFailDateError: false,
        accountNumberInvalidError: false,
        customerComplaintInvalidError: false,
        invalidPartNumberError: false,
        invoiceNumberInvalidError: false,
        purchaseDateInvalidError: false,
        claimDateInvalidError: false
      }
    };
  }

  /* 
    handleDataSetting: takes the name and value of input and sets it in the state
   */
  private handleDataSetting = (event: any) => {
    const { name, value } = event.target;

    if (name.includes('customer.')) {
      const customerState = { ...this.state.formData };
      const nameSplit: any = name.split('.');
      customerState.customer[nameSplit[1]] = value;
      this.setState({ formData: customerState }, () => {
        this.validateForm();
        if (nameSplit[1] === 'email') {
          this.validateEmail();
        } else if (nameSplit[1] === 'phone') {
          this.validatePhone();
        }
      });
    } else {
      const customerState = { ...this.state.formData };
      customerState[name] = value;
      if (name === 'accountNumber') {
        const accountName = this.setAccountName(value);
        customerState['accountName'] = accountName;
      }
      this.setState({ formData: customerState }, () => {
        this.validateForm();
        if (name === 'customerComplaint') {
          this.validateComplaint();
        }
      });
    }
  };

  private validateEmail = () => {
    const error = !this.emailRegEx.test(this.state.formData.customer.email);
    const errors = { ...this.state.errors };
    errors.servicerEmailError = error;
    this.setState({ errors });
  };

  private validatePhone = () => {
    const error =
      !this.PhoneNumberRegEx.test(this.state.formData.customer.phone) ||
      !this.PhoneNumberDashesRegEx.test(this.state.formData.customer.phone) ||
      this.state.formData.customer.phone.length > 12;
    const errors = { ...this.state.errors };
    errors.customerPhoneError = error;
    this.setState({ errors });
  };

  private validateComplaint = () => {
    const error = !this.commentsRegEx.test(
      this.state.formData.customerComplaint
    );
    const errors = { ...this.state.errors };
    errors.customerComplaintInvalidError = error;
    this.setState({ errors });
  };

  private isDateInFuture = (date: moment.Moment) => {
    return date.isAfter();
  };

  private setAccountName = (accountNumber: string) => {
    let name = '';
    this.props.userAccounts.map(account => {
      if (account.accountId === accountNumber) {
        name = account.name;
      }
      return;
    });
    return name;
  };

  private setDate = (date: any) => {
    const customerState = { ...this.state.formData };
    customerState.date = date;
    this.setState(
      {
        formData: customerState
      },
      () => {
        this.validateForm();
        const errors = { ...this.state.errors };
        errors.claimDateInvalidError = this.isDateInFuture(
          this.state.formData.date
        );
        this.setState({ errors });
      }
    );
  };

  private setDatePurchased = (datePurchased: any) => {
    const customerState = { ...this.state.formData };
    customerState.datePurchased = datePurchased;
    this.setState(
      {
        formData: customerState
      },
      () => {
        this.validateForm();
        const errors = { ...this.state.errors };
        errors.purchaseDateInvalidError = this.isDateInFuture(
          this.state.formData.datePurchased
        );
        this.setState({ errors });
      }
    );
  };

  private setDateFailed = (dateFailed: any) => {
    const customerState = { ...this.state.formData };
    customerState.dateFailed = dateFailed;
    this.setState(
      {
        formData: customerState
      },
      () => {
        this.validateForm();
        const errors = { ...this.state.errors };
        errors.partFailDateError = this.isDateInFuture(
          this.state.formData.dateFailed
        );
        this.setState({ errors });
      }
    );
  };

  private setDateInstalled = (dateInstalled: any) => {
    const customerState = { ...this.state.formData };
    customerState.dateInstalled = dateInstalled;
    this.setState(
      {
        formData: customerState
      },
      () => {
        this.validateForm();
        const errors = { ...this.state.errors };
        errors.partInstalledDateError = this.isDateInFuture(
          this.state.formData.dateInstalled
        );
        this.setState({ errors });
      }
    );
  };

  /* 
    validateForm
   */
  private validateForm = () => {
    const newComponent = { ...this.state.component };
    newComponent.formValidated = true;
    newComponent.fieldsFailed = [];
    for (const property in this.state.formData) {
      if (this.state.formData.hasOwnProperty(property)) {
        if (property !== 'component') {
          if (
            property === 'date' ||
            property === 'datePurchased' ||
            property === 'dateFailed' ||
            property === 'dateInstalled'
          ) {
            if (!this.state.formData[property]) {
              newComponent.formValidated = false;
              newComponent.fieldsFailed.push(property.toString());
            }
          }
          if (
            typeof this.state.formData[property] === 'string' &&
            property !== 'accountName'
          ) {
            if (this.state.formData[property] === '') {
              newComponent.formValidated = false;
              newComponent.fieldsFailed.push(property.toString());
            }
          } else if (typeof this.state.formData[property] === 'number') {
            if (
              this.state.formData[property] === 0 ||
              this.state[property] === ''
            ) {
              newComponent.formValidated = false;
              newComponent.fieldsFailed.push(property);
            }
          }
        }
      }
    }
    for (const property in this.state.formData.customer) {
      if (this.state.formData.customer.hasOwnProperty(property)) {
        if (typeof this.state.formData.customer[property] === 'string') {
          if (this.state.formData.customer[property] === '') {
            newComponent.formValidated = false;
            newComponent.fieldsFailed.push(property.toString());
          }
        } else if (typeof this.state.formData.customer[property] === 'number') {
          if (
            this.state.formData.customer[property] === 0 ||
            this.state[property] === ''
          ) {
            newComponent.formValidated = false;
            newComponent.fieldsFailed.push(property);
          }
        }
      }
    }

    this.setState({ component: newComponent });
  };

  /* 
    buildFormData
   */
  private buildFormData = () => {
    const formData = {
      accountNumber: this.state.formData.accountNumber,
      companyName: this.state.formData.companyName,
      customerAddress: this.state.formData.customer.address,
      customerCity: this.state.formData.customer.city,
      customerName: this.state.formData.customer.name,
      customerPhone: this.state.formData.customer.phone,
      customerPostalCode: this.state.formData.customer.zipCode,
      email: this.state.formData.customer.email,
      claimDate: this.state.formData.date.format('YYYY-MM-DD'), // yyyy-mm-dd
      dateCall: this.state.formData.dateInstalled.format('YYYY-MM-DD'), // yyyy-mm-dd
      dateFailed: this.state.formData.dateFailed.format('YYYY-MM-DD'), // yyyy-mm-dd
      defect: this.state.formData.customerComplaint,
      invoiceNumber: Number(this.state.formData.invoiceNumber),
      manufacturer: this.state.formData.manufacturer,
      model: this.state.formData.modelNumber,
      partNumber: this.state.formData.partNumber,
      price: Number(this.state.formData.price),
      purchaseDate: this.state.formData.datePurchased.format('YYYY-MM-DD'), // yyyy-mm-dd
      quantity: Number(this.state.formData.quantity),
      serialNumber: this.state.formData.serialNumber
    };
    return formData;
  };

  /* 
    callService
   */
  private callService = (dataForm: IPartClaimCanada) => {
    this.props.onFailed('', { status: false });
    PartClaimService.createPartClaimCanada(dataForm)
      .then(response => {
        this.props.onSuccess();
      })
      .catch(error => {
        if (error.response.status === 400 && error.response.data) {
          this.handleErrors(error.response.data.message);
          // return this.props.onFailed(error.response.data);
        }
        this.props.onFailed('');
      });
  };

  private handleErrors = (errData: string) => {
    // errData = "customerComplaint - Customer complaint should be only contain alphabets and numerics ; accountNumber - The account number does not exist ; customer.servicerEmail - Email entered is not valid ; customer.phone - Phone number is invalid ; partInstalledDate - Part installed date must be a date in the past or in the present ; warrantyClaimDetails - Invalid part numbers, 'asd', 'asd' ; partFailedDate - Part failed date must be a date in the past or in the present"
    // tslint:disable-next-line: prefer-const
    let errors: IFormErrors = FormErrors();
    errData.includes('accountNumber')
      ? (errors['accountNumberInvalidError'] = true)
      : (errors['accountNumberInvalidError'] = false);
    errData.includes('customerPhone')
      ? (errors['customerPhoneError'] = true)
      : (errors['customerPhoneError'] = false);
    errData.includes('dateFailed')
      ? (errors['partFailDateError'] = true)
      : (errors['partFailDateError'] = false);
    errData.includes('claimDate')
      ? (errors['claimDateInvalidError'] = true)
      : (errors['claimDateInvalidError'] = false);
    errData.includes('email')
      ? (errors['servicerEmailError'] = true)
      : (errors['servicerEmailError'] = false);
    errData.includes('defect')
      ? (errors['customerComplaintInvalidError'] = true)
      : (errors['customerComplaintInvalidError'] = false);
    errData.includes('purchaseDate')
      ? (errors.purchaseDateInvalidError = true)
      : (errors.purchaseDateInvalidError = false);
    errData.includes('partNumber')
      ? (errors.invalidPartNumberError = true)
      : (errors.invalidPartNumberError = false);
    // errData.includes('')
    this.setState({ errors });
  };

  public isFormInvalid = (): boolean => {
    const arr = Object.keys(this.state.errors).map(
      key => this.state.errors[key]
    );
    return arr.some(value => value === true);
  };

  public render() {
    const { t } = this.props;
    const { state } = this;
    const { formData } = state;

    return (
      <form className="rp-part-claim-form-ca">
        <div className="form-row basic-info">
          <div className="data-input col-sm-12 col-md-3">
            <span className="label">
              {t('partClaim.accountNumber')}:<span className="required">*</span>
            </span>
            <input
              className={
                this.state.errors?.accountNumberInvalidError
                  ? 'rp-invalid-input-highlight'
                  : ''
              }
              name="accountNumber"
              type="text"
              value={formData.accountNumber}
              onChange={this.handleDataSetting}
            />
            <span className="input-info">
              {this.state.formData.accountName}
              {this.state.formData.accountName ? <br /> : ''}
              <span className="rp-warranty-input-error-internal">
                {this.state.errors?.accountNumberInvalidError
                  ? 'Invalid account number.'
                  : ''}
              </span>
            </span>
          </div>
          <div className="data-input col-sm-12 col-md-3">
            <span className="label">
              {t('common.date')}:<span className="required">*</span>
            </span>
            <div
              className={
                this.state.errors?.claimDateInvalidError
                  ? 'rp-invalid-input-highlight date-range-container'
                  : 'date-range-container'
              }
            >
              <DateSingle onDateSelected={this.setDate} date={formData.date} />
            </div>
            <span className="rp-warranty-input-error">
              {this.state.errors?.claimDateInvalidError ? 'Invalid date.' : ''}
            </span>
          </div>
          <div className="data-input col-sm-12 col-md-3">
            <span className="label">
              {t('formData.companyName')}:<span className="required">*</span>
            </span>
            <input
              className=""
              name="companyName"
              type="text"
              value={formData.companyName}
              onChange={this.handleDataSetting}
            />
          </div>
          {/* Email */}
          <div className="data-input col-sm-12 col-md-3">
            <span className="label">
              {t('common.email')}:<span className="required">*</span>
            </span>
            <input
              className={
                this.state.errors?.servicerEmailError
                  ? 'rp-invalid-input-highlight'
                  : ''
              }
              name="customer.email"
              type="text"
              value={formData.customer.email}
              onChange={this.handleDataSetting}
            />
            <span className="rp-warranty-input-error">
              {this.state.errors?.servicerEmailError
                ? 'Invalid email format.'
                : ''}
            </span>
          </div>
        </div>

        <span className="separator" />

        <div className="form-row customer-data">
          {/* Name */}
          <div className="form-row customer-name">
            <div className="data-input col-12">
              <span className="label">
                {`${t('partClaim.customerName')} (${t(
                  'partClaim.productOwner'
                )})`}
                :<span className="required">*</span>
              </span>
              <input
                className=""
                name="customer.name"
                type="text"
                value={formData.customer.name}
                onChange={this.handleDataSetting}
              />
            </div>
          </div>
          {/* Address */}
          <div className="form-row">
            <div className="data-input col-12">
              <span className="label">
                {`${t('partClaim.customerAddress')} (${t(
                  'partClaim.productOwner'
                )})`}
                :<span className="required">*</span>
              </span>
              <input
                className=""
                name="customer.address"
                type="text"
                value={formData.customer.address}
                onChange={this.handleDataSetting}
              />
            </div>
          </div>
          {/* City and zip */}
          <div className="form-row">
            <div className="data-input col-sm-12 col-md-6">
              <span className="label">
                {`${t('partClaim.customerCity')} / ${t('formData.province')}`}:
                <span className="required">*</span>
              </span>
              <input
                className=""
                name="customer.city"
                type="text"
                value={formData.customer.city}
                onChange={this.handleDataSetting}
              />
            </div>
            <div className="data-input col-sm-12 col-md-6">
              <span className="label">
                {t('partClaim.customerPostalCode')}:
                <span className="required">*</span>
              </span>
              <input
                className=""
                name="customer.zipCode"
                type="text"
                value={formData.customer.zipCode}
                onChange={this.handleDataSetting}
              />
            </div>
          </div>
          <div className="form-row">
            {/* Phone */}
            <div className="data-input col-sm-12 col-md-6">
              <span className="label">
                {t('partClaim.customerPhone')}:
                <span className="required">*</span>
              </span>
              <input
                className={
                  this.state.errors?.customerPhoneError
                    ? 'rp-invalid-input-highlight'
                    : ''
                }
                name="customer.phone"
                type="text"
                value={formData.customer.phone}
                onChange={this.handleDataSetting}
              />
              <span className="input-info">
                {`(${t('partClaim.noDashes')})`}
                <br />
                <span className="rp-warranty-input-error-internal">
                  {this.state.errors?.customerPhoneError
                    ? 'Invalid phone number.'
                    : ''}
                </span>
              </span>
            </div>
          </div>
        </div>

        <span className="separator" />

        <div className="form-row part-details">
          <div className="form-row">
            <div className="data-input col-12">
              <span className="label">
                {t('partClaim.manufacturer')}:
                <span className="required">*</span>
              </span>
              <input
                className=""
                name="manufacturer"
                type="text"
                value={formData.manufacturer}
                onChange={this.handleDataSetting}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="data-input col-sm-12 col-md-6">
              <span className="label">
                {t('partClaim.modelNumber')}:<span className="required">*</span>
              </span>
              <input
                className=""
                name="modelNumber"
                type="text"
                value={formData.modelNumber}
                onChange={this.handleDataSetting}
              />
            </div>
            <div className="data-input col-sm-12 col-md-6">
              <span className="label">
                {t('partClaim.serialNumber')}:
                <span className="required">*</span>
              </span>
              <input
                className=""
                name="serialNumber"
                type="text"
                value={formData.serialNumber}
                onChange={this.handleDataSetting}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="data-input custom-cols">
              <span className="label">
                {t('partClaim.partNumber')}:<span className="required">*</span>
              </span>
              <input
                className={
                  this.state.errors?.invalidPartNumberError
                    ? 'rp-invalid-input-highlight'
                    : ''
                }
                name="partNumber"
                type="text"
                value={formData.partNumber}
                onChange={this.handleDataSetting}
              />
              <span className="rp-warranty-input-error">
                {this.state.errors?.invalidPartNumberError
                  ? 'Invalid part number.'
                  : ''}
              </span>
            </div>
            <div className="data-input custom-cols">
              <span className="label">
                {t('partClaim.quantity')}:<span className="required">*</span>
              </span>
              <input
                className=""
                name="quantity"
                type="number"
                min={1}
                value={Math.abs(formData.quantity).toFixed(0)}
                onChange={this.handleDataSetting}
              />
            </div>
            <div className="data-input custom-cols">
              <span className="label">
                {t('partClaim.price')}:<span className="required">*</span>
              </span>
              <input
                className=""
                name="price"
                type="number"
                min={0}
                value={Math.abs(formData.price).toString()}
                onChange={this.handleDataSetting}
              />
            </div>
            <div className="data-input custom-cols">
              <span className="label">
                {t('partClaim.invoiceNumber')}:
                <span className="required">*</span>
              </span>
              <input
                className=""
                name="invoiceNumber"
                type="number"
                min={1}
                value={Math.abs(formData.invoiceNumber).toFixed(0)}
                onChange={this.handleDataSetting}
              />
            </div>
            <div className="data-input custom-cols" />
          </div>
        </div>

        <span className="separator" />

        <div className="form-row part-data">
          {/* Parts installed and dates */}
          <div className="form-row">
            <div className="data-input col-12">
              <span className="label">
                {t('partClaim.purchaseDate')}:
                <span className="required">*</span>
              </span>
              <div
                className={
                  this.state.errors?.purchaseDateInvalidError
                    ? 'rp-invalid-input-highlight date-range-container'
                    : 'date-range-container'
                }
              >
                <DateSingle
                  onDateSelected={this.setDatePurchased}
                  date={formData.datePurchased}
                />
              </div>
              <span className="rp-warranty-input-error">
                {this.state.errors?.purchaseDateInvalidError
                  ? 'Invalid date.'
                  : ''}
              </span>
            </div>
          </div>
          <div className="form-row">
            <div className="data-input col-sm-12 col-md-6">
              <span className="label">
                {t('partClaim.datePartsInstalled')}:
                <span className="required">*</span>
              </span>
              <div
                className={
                  this.state.errors?.partInstalledDateError
                    ? 'rp-invalid-input-highlight date-range-container'
                    : 'date-range-container'
                }
              >
                <DateSingle
                  onDateSelected={this.setDateInstalled}
                  date={formData.dateInstalled}
                />
              </div>
              <span className="rp-warranty-input-error">
                {this.state.errors?.partInstalledDateError
                  ? 'Invalid date.'
                  : ''}
              </span>
            </div>
            <div className="data-input col-sm-12 col-md-6">
              <span className="label">
                {t('partClaim.datePartsFailed')}:
                <span className="required">*</span>
              </span>
              <div
                className={
                  this.state.errors?.partFailDateError
                    ? 'rp-invalid-input-highlight date-range-container'
                    : 'date-range-container'
                }
              >
                <DateSingle
                  onDateSelected={this.setDateFailed}
                  date={formData.dateFailed}
                />
              </div>
              <span className="rp-warranty-input-error">
                {this.state.errors?.partFailDateError ? 'Invalid date.' : ''}
              </span>
            </div>
          </div>
          <div className="form-row">
            <div className="data-input col-12">
              <span className="label">
                {t('partClaim.natureOfDefect')}:
                <span className="required">*</span>
              </span>
              <span className="label-info">{`(${t(
                'partClaim.natureDescription'
              )})`}</span>
              <textarea
                className={
                  this.state.errors?.customerComplaintInvalidError
                    ? 'rp-invalid-input-highlight'
                    : ''
                }
                name="customerComplaint"
                value={formData.customerComplaint}
                onChange={this.handleDataSetting}
              />
              <span className="input-info">
                {`(${t('partClaim.noSymbols')})`}
                <br />
                <span className="rp-warranty-input-error-internal">
                  {this.state.errors?.customerComplaintInvalidError
                    ? 'Invalid defect format.'
                    : ''}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="data-row buttons">
          <Button
            label={t('partClaim.submitClaim')}
            buttonClass="primary-custom-button submit-button"
            textTransform="uppercase"
            disabled={
              !this.state.component.formValidated || this.isFormInvalid()
            }
            action={() => {
              this.callService(this.buildFormData());
            }}
          />
        </div>
      </form>
    );
  }
}

export default WarrantyClaimFormCA;

interface IFormErrors {
  servicerEmailError: boolean;
  customerPhoneError: boolean;
  partInstalledDateError: boolean;
  partFailDateError: boolean;
  accountNumberInvalidError: boolean;
  customerComplaintInvalidError: boolean;
  purchaseDateInvalidError: boolean;
  invoiceNumberInvalidError?: boolean;
  invalidPartNumberError: boolean;
  claimDateInvalidError: boolean;
}

function FormErrors() {
  return {
    servicerEmailError: false,
    customerPhoneError: false,
    partInstalledDateError: false,
    partFailDateError: false,
    accountNumberInvalidError: false,
    customerComplaintInvalidError: false,
    invalidPartNumberError: false,
    invoiceNumberInvalidError: false,
    purchaseDateInvalidError: false,
    claimDateInvalidError: false
  };
}
